import * as React from 'react';
import reactModal from '@prezly/react-promise-modal';
import { ThemeProvider } from '@mui/material/styles';
import { Button, DialogActions, DialogContent } from '@mui/material';

import theme from '@/src/theme';
import Dialog from '@/components/molecules/Dialog/Dialog';

type Props = {
  isOpen: boolean;
  onConfirm: React.ReactEventHandler;
  onCancel: React.ReactEventHandler;
  title: string;
  message: string;
  showCancel?: boolean;
  actions?: React.ReactHTMLElement<any>[];
  t: Function;
};

const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  showCancel = true,
  actions,
  t,
}: Props) => {
  const funcsByAction = {
    confirm: onConfirm,
    cancel: onCancel,
  };

  const customButtonsActions = buttons =>
    buttons.map(button =>
      !button.props.action ||
      !Object.keys(funcsByAction).includes(button.props.action)
        ? button
        : React.cloneElement(button, {
            onClick: funcsByAction[button.props.action],
          }),
    );

  return (
    <Dialog open={isOpen} onClose={onCancel} title={title}>
      <DialogContent sx={{ display: 'flex', alignItems: 'center' }}>
        <ul>{message}</ul>
      </DialogContent>
      <DialogActions>
        {actions?.length
          ? customButtonsActions(actions)
          : [
              showCancel && (
                <Button
                  key="action_cancel"
                  sx={{ mr: 2 }}
                  variant="outlined"
                  color="primary"
                  onClick={onCancel}
                  data-cy="btn-cancel-action"
                >
                  {t('action_cancel')}
                </Button>
              ),
              // eslint-disable-next-line
              <Button
                key="action_validate"
                variant="contained"
                color="secondary"
                onClick={onConfirm}
                data-cy="btn-confirm-action"
              >
                {t('action_validate')}
              </Button>,
            ]}
      </DialogActions>
    </Dialog>
  );
};

export default ({ title, message, t, ...rest }) =>
  reactModal(({ show, onSubmit, onDismiss }) => (
    <ThemeProvider theme={theme}>
      <ConfirmDialog
        title={title}
        message={message}
        isOpen={show}
        onConfirm={onSubmit}
        onCancel={onDismiss}
        t={t}
        {...rest}
      />
    </ThemeProvider>
  ));
